/**
 * Standard object sizes (#32) — the real-world things a leather pattern gets
 * built around.
 *
 * A card slot is sized to a card; a note sleeve to a banknote; a tracker pocket
 * to the tracker. These are the dimensions a maker would otherwise look up and
 * type in every time, and typing them from memory is where a 1 mm error in a
 * card slot comes from.
 *
 * ## What is in the list, and what is not
 *
 * Every entry is a PUBLISHED standard or a manufacturer's stated dimension —
 * ISO/IEC 7810 for cards, the ECB for euro notes, the US Mint for coins. None
 * of them is measured off a photo or averaged from a forum post.
 *
 * Deliberately absent: phones, tablets and laptops. Those change every year and
 * differ per model, so a list of them is out of date before it ships and wrong
 * in a way a maker only discovers after cutting. A `hint` on each entry says
 * what the number is nominal for, because "credit card" is a standard but
 * "business card" is a convention that differs by continent.
 *
 * These are the OBJECT's size. Ease, turn-back and the thickness of the leather
 * are the maker's to add — the app has no business guessing at those, and a
 * slot cut to exactly 85.6 mm is a slot a card does not go into.
 */

export interface StandardSize {
  /** Stable id — safe to persist, unlike the label. */
  id: string;
  /** What it is, as a maker would ask for it. */
  label: string;
  /** Grouped in the picker so a long list stays scannable. */
  group: string;
  widthMm: number;
  heightMm: number;
  /** True when the object is a circle and the two axes are one diameter —
   *  lets the picker stay useful when an ellipse / round shape is selected. */
  round?: boolean;
  /** Where the number comes from, and what it is nominal for. Shown in the
   *  option's title so the source travels with the value. */
  hint: string;
  /**
   * Corner radius the object is normally made with, in mm. Only on entries
   * whose shape is a rounded rectangle.
   *
   * Picking the entry sets it as the tool's draw-time corner radius, so the
   * shape is drawn with its corners already rounded (#1069) — still adjustable,
   * like corners rounded by hand.
   *
   * `matchStandardSize` ignores it either way: the radius is how a thing is
   * usually made, not part of how big it is.
   */
  cornerRadiusMm?: number;
  /**
   * True when the object is a thing you'd CUT FROM leather, rather than a thing
   * you'd cut a slot or a pocket to hold.
   *
   * A coaster is a leather part. A credit card, a banknote and a sheet of A4
   * are not — you size a slot to them, and offering "make a part shaped like a
   * banknote" is offering a mistake. Only the leather-part list is filtered by
   * this; construction lines and cut-outs still see everything, because both
   * are ways of describing the OBJECT rather than the leather.
   */
  makesLeatherPiece?: boolean;
}

/** Where a size list is being shown, which decides what belongs in it. */
export type StandardSizeContext = "construction" | "cutout" | "leatherPiece";

export const STANDARD_SIZES: readonly StandardSize[] = [
  // — Cards —
  {
    id: "card-id1",
    label: "Credit / ID card",
    group: "Cards",
    widthMm: 85.6,
    heightMm: 53.98,
    cornerRadiusMm: 3.18,
    hint: "ISO/IEC 7810 ID-1, with its 3.18 mm (1/8 in) corners — bank cards, driving licences, most ID cards",
  },
  {
    id: "card-business-us",
    label: "Business card (US)",
    group: "Cards",
    widthMm: 88.9,
    heightMm: 50.8,
    hint: "3.5 × 2 in — the North American convention",
  },
  {
    id: "card-business-eu",
    label: "Business card (EU)",
    group: "Cards",
    widthMm: 85,
    heightMm: 55,
    hint: "The common European convention; close to but not the same as ID-1",
  },
  {
    id: "card-playing",
    label: "Playing card (poker)",
    group: "Cards",
    widthMm: 63.5,
    heightMm: 88.9,
    cornerRadiusMm: 3,
    hint: "2.5 × 3.5 in with 3 mm corners — poker size; bridge size is 57.15 mm wide",
  },
  {
    id: "passport-id3",
    label: "Passport",
    group: "Cards",
    widthMm: 125,
    heightMm: 88,
    hint: "ISO/IEC 7810 ID-3 — the booklet closed",
  },

  // — Money —
  {
    id: "note-usd",
    label: "US banknote",
    group: "Money",
    widthMm: 155.96,
    heightMm: 66.29,
    hint: "6.14 × 2.61 in — every denomination is the same size",
  },
  {
    id: "note-eur-20",
    label: "Euro note (€20)",
    group: "Money",
    widthMm: 133,
    heightMm: 72,
    hint: "ECB — euro notes differ per denomination; €5 is 120 × 62 mm",
  },

  // — Paper —
  {
    id: "paper-a4",
    label: "A4 sheet",
    group: "Paper",
    widthMm: 210,
    heightMm: 297,
    hint: "ISO 216",
  },
  {
    id: "paper-letter",
    label: "US Letter sheet",
    group: "Paper",
    widthMm: 215.9,
    heightMm: 279.4,
    hint: "8.5 × 11 in",
  },

  // — Coasters —
  // Both shapes live in one group so the round and square versions of the same
  // drink mat sit together; the round-shape filter splits them by `round`.
  // Sizes are the trade's common ones rather than a published standard, and
  // the hints say so.
  {
    id: "coaster-round-3-5in",
    makesLeatherPiece: true,
    label: "Coaster — 3.5 in round",
    group: "Coasters",
    widthMm: 88.9,
    heightMm: 88.9,
    round: true,
    hint: "3.5 in — the common pub / bar coaster; a convention, not a standard",
  },
  {
    id: "coaster-round-4in",
    makesLeatherPiece: true,
    label: "Coaster — 4 in round",
    group: "Coasters",
    widthMm: 101.6,
    heightMm: 101.6,
    round: true,
    hint: "4 in — the larger common coaster; a convention, not a standard",
  },
  {
    id: "coaster-square-3-5in",
    makesLeatherPiece: true,
    label: "Coaster — 3.5 in square",
    group: "Coasters",
    widthMm: 88.9,
    heightMm: 88.9,
    cornerRadiusMm: 6.35,
    hint: "3.5 in square with 1/4 in corners — a convention, not a standard",
  },
  {
    id: "coaster-square-4in",
    makesLeatherPiece: true,
    label: "Coaster — 4 in square",
    group: "Coasters",
    widthMm: 101.6,
    heightMm: 101.6,
    cornerRadiusMm: 9.525,
    hint: "4 in square with 3/8 in corners — a convention, not a standard",
  },

  // — Round —
  {
    id: "airtag",
    label: "AirTag",
    group: "Round",
    widthMm: 31.9,
    heightMm: 31.9,
    round: true,
    hint: "Apple's stated diameter; 8.0 mm thick",
  },
  {
    id: "coin-quarter",
    label: "US quarter",
    group: "Round",
    widthMm: 24.26,
    heightMm: 24.26,
    round: true,
    hint: "US Mint stated diameter",
  },
  {
    id: "battery-aa",
    label: "AA battery",
    group: "Round",
    widthMm: 14.5,
    heightMm: 14.5,
    round: true,
    hint: "IEC R6 diameter; 50.5 mm long",
  },
];

/** Look one up by id. Returns null for an unknown id rather than throwing —
 *  a stale id from an older build must not take the panel down. */
export function standardSizeById(id: string): StandardSize | null {
  return STANDARD_SIZES.find((s) => s.id === id) ?? null;
}

/**
 * The entries a shape of this kind can take its size from.
 *
 * The shape decides, in BOTH directions: a round shape is offered only round
 * objects, and a rectangle only rectangular ones. A card's 85.6 × 53.98
 * describes a rectangle — applied to an ellipse it makes an oval that is the
 * size of nothing — and an AirTag's 31.9 mm is a diameter, so a 31.9 mm square
 * is not an AirTag either. Offering the wrong family invites exactly those
 * mistakes, and with round and square coasters at matching dimensions it also
 * makes the picker ambiguous about which one a shape is.
 *
 * A rounded-corner rectangle is still a RECTANGLE — the corners don't change
 * which family it belongs to.
 */
export function standardSizesFor(
  round: boolean,
  context: StandardSizeContext = "construction",
): StandardSize[] {
  return STANDARD_SIZES.filter(
    (s) =>
      (s.round === true) === round &&
      (context !== "leatherPiece" || s.makesLeatherPiece === true),
  );
}

/** Every size this context offers, both shape families — what the searchable
 *  list shows before the maker has narrowed it to a shape. */
export function standardSizesInContext(
  context: StandardSizeContext,
): StandardSize[] {
  return STANDARD_SIZES.filter(
    (s) => context !== "leatherPiece" || s.makesLeatherPiece === true,
  );
}

/**
 * Filter a size list by a typed query.
 *
 * Matches the label, the group and the HINT, because the hint is where the
 * source lives — typing "ISO" should find the card, and "pub" the coaster.
 * Also matches the dimensions as typed in mm, so "101" finds the 4 in coaster
 * without the maker knowing what it is called.
 */
export function searchStandardSizes(
  sizes: readonly StandardSize[],
  query: string,
): StandardSize[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...sizes];
  const terms = q.split(/\s+/);
  return sizes.filter((s) => {
    const haystack =
      `${s.label} ${s.group} ${s.hint} ${s.widthMm} ${s.heightMm}`.toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });
}

/** The list grouped for a `<select>`, in declaration order. `round` narrows it
 *  to what a round shape can use — see `standardSizesFor`. */
export function standardSizeGroups(
  round = false,
  context: StandardSizeContext = "construction",
): Array<{ group: string; sizes: StandardSize[] }> {
  const out: Array<{ group: string; sizes: StandardSize[] }> = [];
  for (const size of standardSizesFor(round, context)) {
    const last = out[out.length - 1];
    if (last && last.group === size.group) last.sizes.push(size);
    else out.push({ group: size.group, sizes: [size] });
  }
  return out;
}

/**
 * Which entry, if any, the current width/height pair came from.
 *
 * Compared with a tolerance rather than exactly: the panel round-trips these
 * through a millimetre input that may display fewer decimals than ID-1's
 * 53.98, so an exact match would make the picker forget its own selection the
 * moment the field re-rendered.
 *
 * `round` must be the SAME flag the picker was built with. Matching a wider set
 * than was offered would set the `<select>` to a value it has no option for,
 * and a select in that state shows blank — so an ellipse that happened to be
 * card-sized would read as neither "Credit / ID card" nor "Custom".
 */
export function matchStandardSize(
  widthMm: number | null,
  heightMm: number | null,
  opts: {
    round?: boolean;
    tolMm?: number;
    context?: StandardSizeContext;
  } = {},
): StandardSize | null {
  if (widthMm == null || heightMm == null) return null;
  const { round = false, tolMm = 0.05, context = "construction" } = opts;
  // Scoped to the shape's own family, so the round and square coasters — which
  // share their dimensions exactly — can never be confused for one another.
  return (
    standardSizesFor(round, context).find(
      (s) =>
        Math.abs(s.widthMm - widthMm) <= tolMm &&
        Math.abs(s.heightMm - heightMm) <= tolMm,
    ) ?? null
  );
}
